// /account/sessions — 登入裝置管理 (spec 20 §4).
// is_current is judged SERVER-SIDE via the JWT rtid claim; the client never compares
// token hashes locally (20 §4.1 / E2). All is_current=false → show "無法判定目前裝置".
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ApiError, apiFetch, refreshAccessToken } from '../../lib/api';
import { useAuth } from '../../auth/AuthContext';
import { formatRelativeUs, formatTimestampUs } from '../../lib/format';

interface SessionRow {
  token_hash: string;
  client_id: string;
  device_label: string | null;
  issued_at: number;
  last_used: number | null;
  ip: string | null;
  is_current: boolean;
}

const PLATFORM_LABEL: Record<string, string> = {
  'c-ls-home-ios': 'iOS App',
  'c-ls-ios': 'iOS App',
  'c-ls-home-android': 'Android App',
  'c-ls-android': 'Android App',
  'c-ls-aura-ios': 'Aura iOS App',
  'c-ls-aura-android': 'Aura Android App',
  'c-ls-home-web': 'Web',
  'c-ls-web': 'Web',
};

/**
 * client_id → 人話平台標籤（iOS `sessionPlatformLabel` / Android `sessionPlatformLabel` parity）。
 *
 * ⚠️ prod 實資料裡有 `c-livingstyle-home-web`（dev 另有 6 個），它不在上面的精確表裡 ——
 * 舊版直接 `PLATFORM_LABEL[client_id] || client_id`，會把**內部 client id 原樣當成「裝置名稱」
 * 印給使用者看**。品牌換名時 client_id 會整批換字首，所以精確表一定會漏，必須有後綴推斷。
 *
 * 最後仍保留原字串（不用一句「其他裝置」把所有未知抹成同一列）：對**沒有** device_label 的
 * 舊 session 而言，client_id 是這頁唯一能區分兩列的欄位。
 */
export function sessionPlatformLabel(clientId: string | null | undefined): string {
  if (clientId && PLATFORM_LABEL[clientId]) return PLATFORM_LABEL[clientId];
  if (!clientId) return '—';
  if (clientId.endsWith('-web')) return 'Web';
  if (clientId.endsWith('-ios')) return 'iOS App';
  if (clientId.endsWith('-android')) return 'Android App';
  return clientId;
}

/** 一列 session 要顯示的名稱：device_label（使用者自己取的機器名）優先，沒有才退平台標籤。
 *  ⚠️ 沒有標籤的舊 session **補不回來**（writer 是 restful-api!558 才加的），所以後備路徑不是
 *  暫時的：舊 session 要等 30 天 refresh TTL 自然汰換。 */
export function sessionDisplayLabel(row: Pick<SessionRow, 'device_label' | 'client_id'>): string {
  const l = row.device_label?.trim();
  return l ? l : sessionPlatformLabel(row.client_id);
}

/**
 * 這批 session 是否**證明**伺服端已經是「撤銷立即失效」的版本。
 *
 * 依據：`device_label` 的唯一 writer 與「撤銷即時吊銷 access token」是 restful-api!558 同一支
 * MR、同一顆 binary，所以任何一列帶著標籤即代表伺服端含該修正。
 *
 * 反過來**不成立**：全部沒有標籤，可能是伺服端還沒更新，也可能只是還沒有人用新版登入過
 * （部署當下 prod 1127 筆全是舊的）。因此回 false 只能說「無法確認」，UI 必須採保守說法。
 * 這個不對稱是刻意的 —— 它只會讓 UI 低估自己的即時性，永遠不會宣稱一個做不到的保證。
 */
export function sessionRevocationIsProvenImmediate(rows: readonly SessionRow[]): boolean {
  return rows.some((r) => (r.device_label?.trim() ?? '') !== '');
}

/** 撤銷失敗時的重試上限與退避（DELETE 冪等，見 revoke()）。 */
const REVOKE_MAX_ATTEMPTS = 3;
const REVOKE_BASE_BACKOFF_MS = 400;

export default function SessionsPage() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const [rows, setRows] = useState<SessionRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyHash, setBusyHash] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // 撤銷失敗訊息**不能**共用 `error`：revoke 的 finally 會 `await load()`，而 load() 第一行是
  // `setError(null)` —— 撤銷錯誤剛設好就被清掉，畫面上什麼都不會出現。這是先於本次修改就
  // 存在的缺陷（一般錯誤同樣消音），503 只是讓它變得危險：使用者看不到「尚未登出」，
  // 只看到清單刷新，等同於默認撤銷成功了。
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [keepCurrent, setKeepCurrent] = useState(true);
  const [allBusy, setAllBusy] = useState(false);
  /** server 還有下一頁（meta.next_cursor）。prod 這個帳號有 1127 個 session，而這頁一次只拿
   *  得到 100（= 後端上限）—— 不講就是「你看到的是全部」的無聲謊話。iOS `SessionsView` 已修，
   *  web 這半邊之前連 `meta.next_cursor` 都讀不到（`apiFetch` 不回傳），所以是先補 apiFetch。 */
  const [hasMore, setHasMore] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      // single page (limit 100 = backend max); meta.next_cursor tells us whether it is the whole truth
      const res = await apiFetch<SessionRow[]>('/v3/me/sessions', { query: { limit: 100 } });
      setRows(Array.isArray(res.data) ? res.data : []);
      setHasMore(res.nextCursor != null);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'ACCOUNT_INACTIVE') {
        await logout();
        return;
      }
      setError(e instanceof ApiError ? `${e.message}（${e.code}）` : t('common.loadFailedRetry'));
    } finally {
      setLoading(false);
    }
  }, [logout, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const noneCurrent = useMemo(() => (rows ?? []).length > 0 && (rows ?? []).every((r) => !r.is_current), [rows]);

  const labelOf = (row: SessionRow) => sessionDisplayLabel(row);

  const revoke = useCallback(
    async (row: SessionRow) => {
      const provenNow = sessionRevocationIsProvenImmediate(rows ?? []);
      const msg = row.is_current
        ? t('account.revokeCurrentConfirm')
        : provenNow
          ? t('account.revokeOtherConfirmImmediate', { label: labelOf(row) })
          : t('account.revokeOtherConfirm', { label: labelOf(row) });
      if (!window.confirm(msg)) return;
      setBusyHash(row.token_hash);
      setNotice(null);
      setRevokeError(null);
      // 撤銷語意要在**送出前**判定：成功後 load() 會換掉清單，那時剛好把唯一一列有標籤的
      // session 撤走，文案會無故降級。
      const proven = sessionRevocationIsProvenImmediate(rows ?? []);
      try {
        // 503 重試。伺服端的撤銷寫入端是 fail-closed：墓碑寫不進去就回 503，**絕不**回 200
        // 假裝裝置已被踢掉 —— 所以 503 的意思是「這台還沒被登出」，把它當成功就是換個地方
        // 說謊。重試安全：DELETE 冪等，且伺服端把易失的墓碑寫在耐久的 DynamoDB 撤銷之前，
        // 失敗不留半套狀態，重送是乾淨重做。
        for (let attempt = 1; ; attempt++) {
          try {
            await apiFetch(`/v3/me/sessions/${encodeURIComponent(row.token_hash)}`, { method: 'DELETE' });
            break;
          } catch (e) {
            const retriable = e instanceof ApiError && e.status === 503 && attempt < REVOKE_MAX_ATTEMPTS;
            if (!retriable) throw e;
            await new Promise((r) => setTimeout(r, REVOKE_BASE_BACKOFF_MS * 2 ** (attempt - 1)));
          }
        }
        if (row.is_current) {
          // revoking own session == logout this device (20 §4.2)
          await logout();
          return;
        }
        setNotice(
          proven
            ? t('account.revokedOtherNoticeImmediate', { label: labelOf(row) })
            : t('account.revokedOtherNotice', { label: labelOf(row) })
        );
      } catch (e) {
        // 404 SESSION_NOT_FOUND → row already gone; just refresh (idempotent UX)
        if (e instanceof ApiError && e.code === 'SESSION_NOT_FOUND') {
          // nothing to report — the row is gone, which is what the user asked for
        } else if (e instanceof ApiError && e.status === 503) {
          setNotice(null);
          setRevokeError(t('account.revokeIncomplete'));
        } else {
          setNotice(null);
          setRevokeError(e instanceof ApiError ? `${e.message}（${e.code}）` : t('common.actionFailedRetry'));
        }
      } finally {
        setBusyHash(null);
        await load();
      }
    },
    [load, logout, rows, t]
  );

  const logoutAll = useCallback(async () => {
    const msg = keepCurrent
      ? t('account.logoutAllKeepConfirm')
      : t('account.logoutAllConfirm');
    if (!window.confirm(msg)) return;
    setAllBusy(true);
    setNotice(null);
    setError(null);
    try {
      // Web cookie caveat: the refresh cookie is Path=/v3/auth/refresh, so it does NOT
      // accompany this request — the backend cannot identify the current session and
      // keep_current may not hold. We follow the RESPONSE (current_session_kept) as truth.
      const res = await apiFetch<{ sessions_revoked: number; current_session_kept: boolean }>(
        '/v3/me/sessions/logout-all',
        { method: 'POST', body: { keep_current: keepCurrent }, idempotencyKey: crypto.randomUUID() }
      );
      if (!res.data.current_session_kept) {
        await logout();
        return;
      }
      setNotice(t('account.loggedOutOthersNotice', { count: res.data.sessions_revoked }));
      // current refresh chain kept — silently renew access in case of rev:user kick (20 §3.2 pattern)
      try {
        await refreshAccessToken();
      } catch {
        await logout();
        return;
      }
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message}（${e.code}）` : t('common.actionFailedRetry'));
    } finally {
      setAllBusy(false);
    }
  }, [keepCurrent, load, logout, t]);

  return (
    <div style={{ maxWidth: 720, display: 'flex', flexDirection: 'column', gap: 'var(--spacing-8)' }}>
      <div>
        <Link to="/account" className="text-secondary">
          ← {t('account.backToAccount')}
        </Link>
        <h1 style={{ margin: 'var(--spacing-4) 0 0', fontSize: 'var(--font-size-2xl)' }}>{t('account.sessionsTitle')}</h1>
      </div>

      <div className="card">
        {loading && <p className="text-secondary">{t('common.loading')}</p>}
        {!loading && error && (
          <div>
            <p className="error-text">{error}</p>
            <button className="btn-ghost" onClick={() => void load()}>
              {t('common.retry')}
            </button>
          </div>
        )}
        {notice && <p style={{ color: 'var(--color-semantic-success)', fontSize: 'var(--font-size-md)' }}>{notice}</p>}
        {revokeError && <p className="error-text">{revokeError}</p>}
        {!loading && !error && rows && rows.length === 0 && <p className="text-secondary">{t('account.sessionsEmpty')}</p>}
        {noneCurrent && (
          <p className="text-secondary" style={{ fontSize: 'var(--font-size-md)' }}>
            {t('account.cannotDetermineCurrent')}
          </p>
        )}
        {!loading && rows && rows.length > 0 && (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {rows.map((row) => (
              <li
                key={row.token_hash}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 'var(--spacing-4)',
                  padding: 'var(--spacing-5) 0',
                  borderTop: '1px solid var(--color-surface-line)',
                }}
              >
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontWeight: 'var(--font-weight-medium)' }}>
                    {labelOf(row)}{' '}
                    {row.is_current && (
                      <span
                        style={{
                          color: 'var(--color-semantic-info)',
                          fontSize: 'var(--font-size-sm)',
                          border: '1px solid var(--color-semantic-info)',
                          borderRadius: 'var(--radius-full)',
                          padding: '0 var(--spacing-3)',
                        }}
                      >
                        {t('account.currentDevice')}
                      </span>
                    )}
                  </div>
                  <div className="text-secondary" style={{ fontSize: 'var(--font-size-md)' }}>
                    {t('account.sessionMeta', {
                      platform: sessionPlatformLabel(row.client_id),
                      lastUsed: row.last_used !== null ? formatRelativeUs(row.last_used) : '—',
                      ip: row.ip ?? '—',
                    })}
                  </div>
                  <div className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                    {t('account.sessionIssuedAt', { date: formatTimestampUs(row.issued_at) })}
                  </div>
                </div>
                <button
                  className="btn-danger"
                  disabled={busyHash !== null || allBusy}
                  onClick={() => void revoke(row)}
                >
                  {busyHash === row.token_hash ? t('common.processing') : t('account.logoutThisDevice')}
                </button>
              </li>
            ))}
          </ul>
        )}
        {!loading && !error && hasMore && rows && rows.length > 0 && (
          <p className="text-secondary" style={{ fontSize: 'var(--font-size-md)' }}>
            {t('account.sessionsTruncated', { count: rows.length })}
          </p>
        )}
      </div>

      {!loading && rows && rows.length > 0 && (
        <div className="card">
          <h2 style={{ marginTop: 0, fontSize: 'var(--font-size-xl)' }}>{t('account.logoutAll')}</h2>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-5)' }}>
            <input
              type="checkbox"
              style={{ minHeight: 'auto' }}
              checked={keepCurrent}
              onChange={(e) => setKeepCurrent(e.target.checked)}
              disabled={allBusy}
            />
            {t('account.keepThisDevice')}
          </label>
          <button className="btn-danger" disabled={allBusy || busyHash !== null} onClick={() => void logoutAll()}>
            {allBusy ? t('common.processing') : t('account.logoutAll')}
          </button>
        </div>
      )}
    </div>
  );
}
